import type { FC } from 'react';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { useCreateEdmMutation } from 'api/edm/createEdm';
import { useDeleteEdmMutation } from 'api/edm/deleteEdm';
import { useUpdateEdmMutation } from 'api/edm/updateEdm';
import type { EdmStatus, TExpectedDecisionMaker } from 'api/types/edm';

import { CardWrapper } from './styled';

import { EditableEDM } from '../EditableEDM';
import { StaticEDM } from '../StaticEDM';

// TODO: move type to api/types/edm after API connection
export interface ICard {
    status: EdmStatus;
    name: string;
    age: number;
    phones: string[];
    emails: string[];
    addresses: Array<{
        address: string;
        city: string;
        zip: number;
        state: string;
    }>;
    showDnc: boolean | undefined;
}

interface ICardProps {
    isNew?: boolean;
    data: TExpectedDecisionMaker;
    closeNewCard?: () => void;
    showDnc?: boolean;
}

export const Card: FC<ICardProps> = ({
    isNew,
    data,
    closeNewCard,
    showDnc,
}) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const { propertyId } = useParams<{ propertyId: string }>();
    const queryClient = useQueryClient();

    const handleEdmUpdate = async () => {
        await queryClient.invalidateQueries('property-lead');
        await queryClient.invalidateQueries('infinitePropertyLeads');
        await queryClient.invalidateQueries('property-leads');
        setIsEditing(false);
        closeNewCard?.();
    };

    const { mutate: deleteEdm } = useDeleteEdmMutation({
        onSuccess: async () => await handleEdmUpdate(),
    });

    const { mutate: createEdm } = useCreateEdmMutation({
        onSuccess: async () => await handleEdmUpdate(),
    });

    const { mutate: updateEdm } = useUpdateEdmMutation({
        onSuccess: async () => await handleEdmUpdate(),
    });

    return (
        <CardWrapper>
            {isNew && (
                <EditableEDM
                    isNew
                    data={data}
                    onCancel={() => {
                        closeNewCard?.();
                        setIsEditing(false);
                    }}
                    onSave={(updatedData) =>
                        createEdm({
                            data: updatedData,
                            leadId: Number(propertyId),
                        })
                    }
                />
            )}
            {!isNew && !isEditing && (
                <StaticEDM
                    onEditClick={() => setIsEditing(true)}
                    data={data}
                    showDnc={showDnc}
                />
            )}
            {!isNew && isEditing && (
                <EditableEDM
                    data={data}
                    onCancel={() => setIsEditing(false)}
                    onSave={(updatedData) =>
                        updateEdm({ data: updatedData, id: data.edmId })
                    }
                    onDelete={(id) => deleteEdm({ id })}
                />
            )}
        </CardWrapper>
    );
};

Card.defaultProps = {
    isNew: false,
    closeNewCard: undefined,
    showDnc: false,
};
